import {
  toWSDateString,
  useWSModal,
  WSActions,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSForm,
  WSInfoBox,
  WSInputDate,
  WSInputTextarea,
  WSList,
  WSPill,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { DataItem } from "../../../../shared/components/DataItem";
import { useGetNow } from "../../../../shared/hooks/useGetNow";
import {
  expirationExtensionOptions,
  extendExpirationDefaultValues,
  ExtendExpirationFormData,
  getExtendExpirationDate,
  getExtendExpirationValidationSchema
} from "./utils";
import { useMutationUpdateRequirement } from "../../../../query/requirements/mutations/useMutationUpdateRequirement";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { addDays, getStartOfDay } from "../../../../utils/dates";

interface ExtendExpirationDateModalProps {
  onClose: (success?: boolean) => void;
  onSuccess: (requirement: IRequirementResponse) => void;
  requirement: IRequirementResponse;
}

const ExtendExpirationDateModal: React.FC<ExtendExpirationDateModalProps> = ({
  onClose,
  onSuccess,
  requirement
}) => {
  const now = useGetNow();

  const [updateRequirement, updateRequirementMeta] =
    useMutationUpdateRequirement();

  const currentExpirationDate = requirement.expiresAt;

  console.log(currentExpirationDate);

  return (
    <WSForm<ExtendExpirationFormData>
      onSubmit={async formData => {
        const nextDate = getExtendExpirationDate(
          formData.extensionType,
          formData.customDate
        );

        await updateRequirement(
          {
            requirementId: requirement.requirementId,
            reason: formData.reason,
            expiresAt: nextDate
          },
          {
            onSuccess: () => {
              onSuccess(requirement);
              onClose(true);
            }
          }
        );
      }}
      validationSchema={getExtendExpirationValidationSchema(
        currentExpirationDate
      )}
      defaultValues={extendExpirationDefaultValues}
    >
      {({ watch }) => {
        const extensionType = watch("extensionType");
        const customDate = watch("customDate");

        const nextDate = getExtendExpirationDate(extensionType, customDate);

        const isNextDateEarlier =
          nextDate && currentExpirationDate && nextDate < currentExpirationDate;

        const isNextDate90DaysLate = nextDate && nextDate > addDays(now, 90);

        return (
          <WSList gap="L">
            <WSInfoBox>
              <DataItem
                label="Current expiration date"
                value={toWSDateString(currentExpirationDate, "input")}
              />
              <WSText.ParagraphSm color="gray700" mt="L">
                As the client, you have the ability to extend a contractor's
                requirement expiration date by overriding it. Once extended, it
                cannot be reverted. It is recommended that you confirm with the
                contractor that there is already a renewal in progress to avoid
                you and/or the contractor falling out of compliance upon the
                expiration date.
              </WSText.ParagraphSm>
            </WSInfoBox>

            <WSElement>
              <WSForm.Field
                name="extensionType"
                label="New expiration date"
                component={WSSelect}
                componentProps={{
                  options: expirationExtensionOptions
                }}
              />
            </WSElement>
            <WSInfoBox>
              {extensionType === "custom" && (
                <WSForm.Field
                  name="customDate"
                  label="Custom expiration date"
                  component={WSInputDate}
                  componentProps={{
                    minDate: getStartOfDay(1)
                  }}
                  mb="L"
                />
              )}

              {nextDate && (
                <DataItem
                  label="New expiration date"
                  value={toWSDateString(nextDate, "input")}
                />
              )}
            </WSInfoBox>

            {isNextDateEarlier ? (
              <WSAlert
                title="About moving the expiration earlier"
                theme="warning"
              >
                You are about to move the expiration to an earlier date than
                than the original. Please ensure that the necessary
                communication has been provided to the contractor to avoid
                unexpected surprises and payment delays.
              </WSAlert>
            ) : isNextDate90DaysLate ? (
              <WSAlert
                title="New expiration date is over 90 days out"
                theme="warning"
              >
                You are about to move the expiration to a future date over 90
                days out. Please double check the new expiration date.
                Incomplete requirements may result in payment delays.
              </WSAlert>
            ) : null}

            <WSElement>
              <WSFlexBox.CenterY gap="S" mb="M">
                <WSText.ParagraphSm color="gray500">Reason</WSText.ParagraphSm>
                <WSPill text="Private" icon="lock" theme="neutral" />
              </WSFlexBox.CenterY>

              <WSForm.Field
                name="reason"
                component={WSInputTextarea}
                componentProps={{
                  placeholder:
                    "Please provide a reason for the change (optional)."
                }}
              />
              <WSText.ParagraphXs color="gray400" mt="XS">
                Provide a reason for the change (visible to you and your team
                only).
              </WSText.ParagraphXs>
            </WSElement>

            <WSErrorMessage
              contextKey="Other"
              error={updateRequirementMeta.isError}
            />

            <WSActions
              buttons={[
                {
                  label: "Cancel",
                  kind: "Secondary",
                  onClick: () => onClose(false),
                  type: "button"
                },
                {
                  label: "Save changes",
                  kind: "Primary",
                  type: "submit",
                  loading: updateRequirementMeta.isLoading
                }
              ]}
            />
          </WSList>
        );
      }}
    </WSForm>
  );
};

export function useExtendExpirationDateModal() {
  return useWSModal(ExtendExpirationDateModal, {
    title: "Update expiration date",
    size: "M"
  });
}
